import { useState } from 'react';
import { FINDING_NOTE_MAX_LENGTH, MANUAL_TRANSITIONS, type FindingEventView, type FindingStatus, type ManualFindingStatus } from '@ametica/shared';
import { useFindingEvents, useSetFindingStatus } from '../../api/finding-status';
import { useToast } from '../../components/Toast';
import { formatDateTime } from '../../lib/time';
import { RESOLVED_REASON } from './severity';

const ACTION: Record<ManualFindingStatus, { label: string; done: string }> = {
  IGNORED: { label: 'Ignorovať', done: 'Nález označený ako ignorovaný' },
  ACCEPTED_RISK: { label: 'Akceptované riziko', done: 'Nález označený ako akceptované riziko' },
  OPEN: { label: 'Znovu otvoriť', done: 'Nález znovu otvorený' },
};

export const STATUS_LABEL: Record<FindingStatus, string> = {
  OPEN: 'otvorený',
  RESOLVED: 'uzavretý',
  IGNORED: 'ignorovaný',
  ACCEPTED_RISK: 'akceptované riziko',
};

/**
 * Ručné prechody nálezu (ticket 11): tlačidlá podľa `MANUAL_TRANSITIONS`, poznámka
 * pred potvrdením, a prepínač histórie udalostí. Uzavretý (RESOLVED) nález
 * tlačidlá nemá — otvorí ho ďalší sken, ak sa komponent vráti do rozsahu.
 */
export function FindingStatusActions({
  findingId, status, historyOpen, onToggleHistory,
}: { findingId: string; status: FindingStatus; historyOpen: boolean; onToggleHistory: () => void }) {
  const [pending, setPending] = useState<ManualFindingStatus | null>(null);
  const [note, setNote] = useState('');
  const set = useSetFindingStatus(findingId);
  const toast = useToast();
  const allowed = MANUAL_TRANSITIONS[status];

  async function confirm() {
    if (!pending) return;
    try {
      await set.mutateAsync({ status: pending, note: note.trim() || undefined });
      toast(ACTION[pending].done);
      setPending(null);
      setNote('');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Stav sa nepodarilo zmeniť');
    }
  }

  if (pending) {
    return (
      <div className="flex flex-col gap-2">
        <span className="text-[11.5px] text-dim">{ACTION[pending].label} — poznámka (voliteľná):</span>
        <span className="fbox min-h-[32px]">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={FINDING_NOTE_MAX_LENGTH}
            placeholder="prečo — uvidí to každý v histórii"
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') void confirm(); if (e.key === 'Escape') setPending(null); }}
          />
        </span>
        <div className="flex gap-1.5">
          <button type="button" className="btn btn-p h-[28px] px-3 text-xs" onClick={confirm} disabled={set.isPending}>Potvrdiť</button>
          <button type="button" className="btn btn-g h-[28px] px-3 text-xs" onClick={() => setPending(null)} disabled={set.isPending}>Zrušiť</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {allowed.map((to) => (
        <button key={to} type="button" className="btn btn-g h-[28px] px-3 text-xs" onClick={() => setPending(to)}>{ACTION[to].label}</button>
      ))}
      <button type="button" className="text-[11.5px] text-faint underline-offset-2 hover:text-ink hover:underline" onClick={onToggleHistory} aria-expanded={historyOpen}>
        {historyOpen ? 'skryť históriu' : 'história'}
      </button>
    </div>
  );
}

/** Udalosti od prvého výskytu; aktér null = systém (matching po skene alebo po zmene katalógu). */
export function FindingHistory({ findingId }: { findingId: string }) {
  const events = useFindingEvents(findingId);
  if (events.isPending) return <span className="text-[11.5px] text-faint">Načítavam históriu…</span>;
  if (events.isError) return <span className="text-[11.5px] text-sev-critical">Históriu sa nepodarilo načítať: {events.error.message}</span>;
  return (
    <ol className="flex flex-col gap-1.5">
      {events.data.map((e) => <li key={e.id} className="flex flex-wrap items-baseline gap-x-2 text-[11.5px]"><EventLine e={e} /></li>)}
    </ol>
  );
}

function EventLine({ e }: { e: FindingEventView }) {
  return (
    <>
      <span className="mono text-faint">{formatDateTime(e.createdAt)}</span>
      <span className="text-ink">
        {e.fromStatus === null ? 'prvý výskyt' : `${STATUS_LABEL[e.fromStatus]} → ${STATUS_LABEL[e.toStatus]}`}
        {e.reason && <span className="text-dim"> ({RESOLVED_REASON[e.reason]})</span>}
      </span>
      <span className="text-faint">{e.actor ? e.actor.email : 'systém'}</span>
      {e.note && <span className="text-dim">„{e.note}"</span>}
    </>
  );
}
